import { TFunction } from 'i18next'
import { Eye } from 'lucide-react'

import {
    DataTableAction,
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
    createOptionsFormatter,
    OptionItem,
} from '@/components/ui/data-table-customized'

interface SyncLog {
    sync_log_id: number
    sync_date: string
    sync_status: string
    records_fetched: number
    records_created: number
    records_updated: number
    records_skipped: number
    error_message: string | null
    updated_at: string
}

const getSyncStatusOptions = (t: TFunction): OptionItem[] => [
    {
        value: 'success',
        label: t('attendance_sync_logs.sync_status_success'),
        color: 'success',
    },
    {
        value: 'failed',
        label: t('attendance_sync_logs.sync_status_failed'),
        color: 'error',
    },
    {
        value: 'in_progress',
        label: t('attendance_sync_logs.sync_status_in_progress'),
        color: 'warning',
    },
    {
        value: 'pending',
        label: t('attendance_sync_logs.sync_status_pending'),
        color: 'info',
    },
]

export const getSyncLogColumns = (t: TFunction): DataTableColumn<SyncLog>[] => [
    {
        key: 'sync_date',
        title: t('attendance_sync_logs.sync_date'),
        width: 200,
        sortable: true,
        formatter: { type: 'date', format: 'DD/MM/YYYY' },
    },
    {
        key: 'sync_status',
        title: t('attendance_sync_logs.sync_status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({ options: getSyncStatusOptions(t) }),
    },
    {
        key: 'records_fetched',
        title: t('attendance_sync_logs.records_fetched'),
        width: 200,
        sortable: true,
    },
    {
        key: 'records_created',
        title: t('attendance_sync_logs.records_created'),
        width: 100,
        sortable: true,
    },
    {
        key: 'records_updated',
        title: t('attendance_sync_logs.records_updated'),
        width: 100,
        sortable: true,
    },
    {
        key: 'records_skipped',
        title: t('attendance_sync_logs.records_skipped'),
        width: 100,
        sortable: true,
    },
    {
        key: 'error_message',
        title: t('attendance_sync_logs.error_message'),
        width: 150,
        render: (value) => {
            if (!value) return '-'
            return (
                <div className="truncate max-w-[150px]" title={value}>
                    {value}
                </div>
            )
        },
    },
    {
        key: 'updated_at',
        title: t('attendance_sync_logs.updated_at'),
        width: 200,
        sortable: true,
        formatter: { type: 'date', format: 'DD/MM/YYYY HH:mm:ss' },
    },
]

export const getSyncLogActions = ({
    t,
    onViewMetadata,
}: {
    t: TFunction
    onViewMetadata: (row: SyncLog) => void
}): DataTableAction<SyncLog>[] => [
    {
        key: 'view-metadata',
        label: t('attendance_sync_logs.view_metadata'),
        icon: <Eye className="w-4 h-4" />,
        onClick: onViewMetadata,
    },
]

export const getSyncLogFilterItems = (t: TFunction): FilterItemConfig[] => [
    {
        type: 'daterangepicker',
        name: 'from_date',
        names: ['from_date', 'to_date'],
        label: t('attendance_sync_logs.sync_date'),
        cols: 2,
        showPresets: true,
    } as any,
    {
        type: 'select',
        name: 'status',
        label: t('attendance_sync_logs.status'),
        placeholder: t('attendance_sync_logs.select_status'),
        options: [
            { label: t('attendance_sync_logs.all'), value: '' },
            { label: t('attendance_sync_logs.success'), value: 'success' },
            { label: t('attendance_sync_logs.failed'), value: 'failed' },
            {
                label: t('attendance_sync_logs.in_progress'),
                value: 'in_progress',
            },
        ],
    },
]

export const getSyncLogFilterToggles = (t: TFunction): FilterToggle[] => [
    {
        key: 'sync_date',
        label: t('attendance_sync_logs.sync_date'),
    },
    {
        key: 'sync_status',
        label: t('attendance_sync_logs.sync_status'),
    },
    {
        key: 'records_fetched',
        label: t('attendance_sync_logs.records_fetched'),
    },
    {
        key: 'records_created',
        label: t('attendance_sync_logs.records_created'),
    },
    {
        key: 'records_updated',
        label: t('attendance_sync_logs.records_updated'),
    },
    {
        key: 'records_skipped',
        label: t('attendance_sync_logs.records_skipped'),
    },
    {
        key: 'error_message',
        label: t('attendance_sync_logs.error_message'),
    },
    {
        key: 'updated_at',
        label: t('attendance_sync_logs.updated_at'),
    },
]
